import React, { useContext, useState } from 'react'
import { Paper, Typography, TextField, Box, Switch, Collapse } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { useTheme } from '@mui/material/styles'
import { PatternTreeContext } from '../../contexts/PatternTreeContext'

type ValidityWindowSettingsProps = {
  condition: any
  sourceGroupId: string
  viewOnly?: boolean
}

const ValidityWindowSettings = ({
  condition,
  sourceGroupId,
  viewOnly = false,
}: ValidityWindowSettingsProps) => {
  const theme = useTheme()
  const { handleChangeCondition } = useContext(PatternTreeContext)
  const [showInfo, setShowInfo] = useState(false)

  const isEnabled = !!condition.settings?.validityWindow?.candles
  const validityWindow = condition.settings?.validityWindow?.candles ?? ''

  const handleToggleEnabled = () => {
    if (viewOnly) return
    if (isEnabled) {
      // Turn off
      handleChangeCondition(sourceGroupId, condition.id, {
        'settings.validityWindow': undefined,
      })
    } else {
      // Turn on with default of 1
      handleChangeCondition(sourceGroupId, condition.id, {
        'settings.validityWindow': { candles: 1 },
      })
    }
  }

  const handleChange = (value: string) => {
    if (viewOnly) return

    const candles = parseInt(value, 10)
    if (isNaN(candles) || candles < 1 || candles > 10000) return

    handleChangeCondition(sourceGroupId, condition.id, {
      'settings.validityWindow': { candles },
    })
  }

  return (
    <Paper
      sx={{
        p: 2,
        maxWidth: 350,
        background: theme.palette.background.custom1,
        gap: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body1">Validity Window</Typography>
          <InfoIcon
            sx={{ fontSize: '1rem', color: 'text.secondary', cursor: 'pointer' }}
            onClick={() => setShowInfo(!showInfo)}
          />
        </Box>
        {isEnabled && (
          <Switch
            size="small"
            checked={isEnabled}
            disabled={viewOnly}
            onChange={handleToggleEnabled}
          />
        )}
      </Box>
      <Collapse in={showInfo}>
        <Typography variant="body2" color="textSecondary">
          Keep this condition satisfied for a number of candles after it first becomes true. Useful
          when conditions need to overlap but may not occur on the exact same candle.
        </Typography>
      </Collapse>
      {!isEnabled && (
        <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography variant="body2" color="textSecondary">
            Enable
          </Typography>
          <Switch size="small" checked={false} disabled={viewOnly} onChange={handleToggleEnabled} />
        </Box>
      )}

      {isEnabled && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">Stay satisfied for</Typography>
          <TextField
            size="small"
            type="number"
            value={validityWindow}
            onChange={(e) => handleChange(e.target.value)}
            disabled={viewOnly}
            inputProps={{ min: 1, max: 10000 }}
            sx={{
              width: 100,
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                padding: '6px 10px',
              },
            }}
          />
          <Typography variant="body2">candles</Typography>
        </Box>
      )}
    </Paper>
  )
}

export default ValidityWindowSettings
